import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useMemo, useState } from 'react';

import { aggridHeaderClass, SmallTitleSpan } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { defaultWMS1120_R01StockDto, WMS1120_R01StockDto } from '../Dto_WMS1120_R01';
import { useWMS1120_R01Store } from '../Store_WMS1120_R01';

const STOCK_COLUMN_STATE_KEY = 'WMS1120_R01-stock-grid-column-state';

const applyBottomCenterAlign = <T,>(defs: ColDef<T>[], bottomLabelCols: Set<string>): ColDef<T>[] =>
	defs.map((def) => {
		if (!def?.colId || !bottomLabelCols.has(def.colId)) return def;
		return {
			...def,
			cellStyle: (params: { node?: { rowPinned?: string | null } }) =>
				params?.node?.rowPinned === 'bottom' ? { textAlign: 'center' } : null,
		};
	});

interface Props {
	gridRef: React.RefObject<AgGridReact<WMS1120_R01StockDto> | null>;
}

export const BottomTableStock_WMS1120_R01 = ({ gridRef }: Props) => {
	const {
		WMS1120_R01stockList,
		WMS1120_R01stockColumnState,
		setWMS1120_R01StockColumnState,
		WMS1120ScrollTop,
		setWMS1120ScrollTop,
	} = useWMS1120_R01Store();

	const [, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef,
		key: 'lotNo',
		gridData: WMS1120_R01stockList,
		defaultGridData: {} as WMS1120_R01StockDto,
		selectedRow: undefined,
	});

	const baseColumnDefs: ColDef<WMS1120_R01StockDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '입출고구분', colId: 'inoutClassNm', field: 'inoutClassNm', width: 120,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '입고일자', colId: 'transportDt', field: 'transportDt', width: 100,
			valueFormatter: (p) => p.value || '',
		},
		{
			headerName: 'Lot No', colId: 'lotNo', field: 'lotNo', width: 150,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '수량', colId: 'qty', field: 'qty', width: 100,
			headerClass: aggridHeaderClass,
			type: 'numericColumn',
			valueFormatter: (p) => p.value?.toLocaleString() ?? '0',
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
		},
	], []);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<WMS1120_R01StockDto>[] = useMemo(() => {
		const merged = WMS1120_R01stockColumnState === null
			? baseColumnDefs
			: GridCustomColumnDefs<WMS1120_R01StockDto>({
				baseColumnDefs,
				columnState: WMS1120_R01stockColumnState,
				headerNames: {},
				fallbackHeaderClass: aggridHeaderClass,
			});
		return applyBottomCenterAlign(merged, new Set(['inoutClass']));
	}, [WMS1120_R01stockColumnState, settingRowIndex, baseColumnDefs]);

	const pinnedBottomRowData = useMemo(() => ([{
		inoutClass: `${WMS1120_R01stockList.length.toLocaleString()}건`,
		qty: WMS1120_R01stockList.reduce((sum, item) => sum + (item.qty || 0), 0).toLocaleString(),
	}]), [WMS1120_R01stockList]);

	return (
		<div className="h-full flex flex-col">
			<SmallTitleSpan>자재·재공품 Lot 입고 근거</SmallTitleSpan>
			<div className="flex-1 min-h-0">
				<GridContextWrapper<WMS1120_R01StockDto>
					enableCellCalculation
					gridRef={gridRef}
					paddedGridData={paddedGridData}
					initSelected={() => {}}
					defaultRowData={defaultWMS1120_R01StockDto}
					baseColumnDefs={WMS1120_R01stockColumnState ? columnDefs : baseColumnDefs}
					defaultColumnState={defaultColumnState}
					setColumnState={setWMS1120_R01StockColumnState}
					localStorageKey={STOCK_COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Touch<WMS1120_R01StockDto>
						ref={gridRef}
						rowData={paddedGridData}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						getRowId={getRowId}
						rowClassRules={createRowClassRules<WMS1120_R01StockDto>({
							idKey: 'valueIndex',
							selectedList: [],
							deletedList: [],
						})}
						onCellKeyDown={undefined}
						pinnedBottomRowData={pinnedBottomRowData}
						setHoveredRowIndex={setHoveredRowIndex}
						columnStateKey={STOCK_COLUMN_STATE_KEY}
						setColumnState={setWMS1120_R01StockColumnState}
						scrollTop={WMS1120ScrollTop.stock}
						setScrollTop={(top) => setWMS1120ScrollTop('stock', top)}
					/>
				</GridContextWrapper>
			</div>
		</div>
	);
};
